import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Mail, Phone, MapPin, Loader2 } from "lucide-react";

const STATUS_OPTS = ["new", "contacted", "closed"];

export default function AdminLeads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads");
      setItems(data);
    } catch { toast.error("Failed to load leads"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const changeStatus = async (lead, status) => {
    try {
      await api.put(`/admin/leads/${lead.id}/status?status=${status}`);
      toast.success("Status updated");
      await load();
      if (selected?.id === lead.id) setSelected({ ...selected, status });
    } catch { toast.error("Update failed"); }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete lead from ${lead.full_name}?`)) return;
    try { await api.delete(`/admin/leads/${lead.id}`); toast.success("Deleted"); await load(); if (selected?.id === lead.id) setSelected(null); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-leads">
      <div className="mb-8">
        <h1 className="font-display text-4xl font-black text-[color:var(--mia-navy)] tracking-tight">Contact Leads</h1>
        <p className="text-[color:var(--mia-muted)] mt-2">{items.length} enquir{items.length === 1 ? "y" : "ies"} received.</p>
      </div>
      <div className="grid lg:grid-cols-[1.4fr,1fr] gap-6">
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          {loading ? <div className="p-10 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</div> :
            items.length === 0 ? <div className="p-10 text-center text-[color:var(--mia-muted)]">No leads yet.</div> :
            <ul className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
              {items.map((l) => (
                <li key={l.id} className={`p-5 cursor-pointer hover:bg-slate-50 ${selected?.id === l.id ? "bg-slate-50" : ""}`} onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-[color:var(--mia-navy)]">{l.full_name}</div>
                    <span className={`text-[10px] px-2 py-1 rounded-full uppercase tracking-widest font-bold ${l.status === "new" ? "bg-blue-100 text-blue-700" : l.status === "contacted" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{l.status}</span>
                  </div>
                  <div className="text-sm text-[color:var(--mia-muted)] mt-1">{l.email}</div>
                  <div className="text-sm text-[color:var(--mia-muted)] mt-1 line-clamp-1">{l.message}</div>
                  <div className="text-xs text-[color:var(--mia-muted)] mt-2">{new Date(l.created_at).toLocaleString()}</div>
                </li>
              ))}
            </ul>
          }
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6 h-fit sticky top-6">
          {!selected ? (
            <div className="text-[color:var(--mia-muted)] text-center py-16">Select a lead to view details.</div>
          ) : (
            <div data-testid="lead-detail">
              <div className="font-display text-2xl font-black text-[color:var(--mia-navy)]">{selected.full_name}</div>
              {selected.company && <div className="text-sm text-[color:var(--mia-muted)]">{selected.company}</div>}
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-[color:var(--mia-blue)]" /> <a href={`mailto:${selected.email}`} className="hover:underline">{selected.email}</a></div>
                {selected.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-[color:var(--mia-blue)]" /> {selected.phone}</div>}
                {selected.project_location && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[color:var(--mia-blue)]" /> {selected.project_location}</div>}
              </div>
              {selected.service_interest && <div className="mt-4"><div className="text-xs uppercase tracking-widest text-[color:var(--mia-muted)]">Service Interest</div><div className="mt-1 font-semibold text-[color:var(--mia-navy)]">{selected.service_interest}</div></div>}
              {selected.budget && <div className="mt-4"><div className="text-xs uppercase tracking-widest text-[color:var(--mia-muted)]">Budget</div><div className="mt-1 font-semibold text-[color:var(--mia-navy)]">{selected.budget}</div></div>}
              <div className="mt-4"><div className="text-xs uppercase tracking-widest text-[color:var(--mia-muted)]">Message</div><p className="mt-1 whitespace-pre-line text-sm">{selected.message}</p></div>

              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest text-[color:var(--mia-muted)] mb-2">Status</div>
                <div className="flex gap-2">
                  {STATUS_OPTS.map((s) => (
                    <button key={s} onClick={() => changeStatus(selected, s)} className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest border ${selected.status === s ? "bg-[color:var(--mia-navy)] text-white border-[color:var(--mia-navy)]" : "border-slate-200 text-[color:var(--mia-navy)] hover:border-[color:var(--mia-navy)]"}`} data-testid={`status-${s}`}>{s}</button>
                  ))}
                </div>
              </div>
              <button onClick={() => remove(selected)} className="mt-6 text-sm text-red-500 hover:text-red-700 flex items-center gap-2" data-testid="lead-delete"><Trash2 className="w-4 h-4" /> Delete lead</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
